import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  Input,
  Button,
  Tag,
  Tooltip,
  Modal,
  Skeleton,
  message,
  Select,
} from 'antd'
import { SearchOutlined, EyeOutlined, MessageOutlined } from '@ant-design/icons'
import styled from 'styled-components/macro'
import { supplierPortalTheme } from 'src/branding/supplierPortalTheme'
import moment from 'moment'
import { API } from 'src/utils/interceptor'
import { useSelector } from 'react-redux'

const { Option } = Select

const SentMessagesTab = () => {
  const account = useSelector(state => state.auth.account)
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchText, setSearchText] = useState('')
  const [dateFilter, setDateFilter] = useState('all') // 'all', 'last30'
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [detailsModalVisible, setDetailsModalVisible] = useState(false)

  // Supplier IDs available to the user
  const supplierIds = useMemo(() => {
    return account?.supplier?.map(s => s._id).filter(Boolean) || []
  }, [account])

  // Use first supplier ID
  const supplierId = supplierIds.length > 0 ? supplierIds[0] : null

  // Fetch messages on mount
  useEffect(() => {
    if (supplierId) {
      fetchMessages()
    }
  }, [supplierId])

  const fetchMessages = async () => {
    setLoading(true)
    try {
      const response = await API.get('api2', 'supplier/marketing/messages')
      if (response.success) {
        setMessages(response.messages || [])
      } else {
        message.error('Failed to load messages')
      }
    } catch (error) {
      console.error('Error fetching messages:', error)
      message.error('Failed to load messages. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Filter messages based on search and date filter
  const filteredMessages = useMemo(() => {
    let filtered = messages

    // Apply date filter
    if (dateFilter === 'last30') {
      const thirtyDaysAgo = moment().subtract(30, 'days')
      filtered = filtered.filter(msg =>
        moment(msg.sentAt).isAfter(thirtyDaysAgo),
      )
    }

    // Apply search filter
    if (searchText.trim()) {
      const searchTerm = searchText.toLowerCase().trim()
      filtered = filtered.filter(msg => {
        const messageTextMatch = msg.messageText
          ?.toLowerCase()
          .includes(searchTerm)
        const venueMatch = msg.venueIds?.some(venue =>
          venue?.title?.toLowerCase().includes(searchTerm),
        )
        return messageTextMatch || venueMatch
      })
    }

    return filtered
  }, [messages, searchText, dateFilter])

  // Calculate status counts for each message
  const getStatusCounts = message => {
    const successCount = message.notificationSentCount || 0
    const totalCount = message.totalRecipients || 0
    const failedCount = totalCount - successCount
    return { successCount, failedCount, totalCount }
  }

  // Get send type badge color
  const getSendTypeColor = messageType => {
    switch (messageType) {
      case 'broadcast':
        return 'orange'
      case 'one-to-one':
        return 'blue'
      default:
        return 'default'
    }
  }

  // Format send type for display
  const formatSendType = messageType => {
    switch (messageType) {
      case 'broadcast':
        return 'Broadcast'
      case 'one-to-one':
        return 'One-to-One'
      default:
        return messageType
    }
  }

  const handleViewDetails = message => {
    setSelectedMessage(message)
    setDetailsModalVisible(true)
  }

  const columns = [
    {
      title: 'Sent At',
      dataIndex: 'sentAt',
      key: 'sentAt',
      width: 180,
      render: date => (date ? moment(date).format('DD MMM YYYY, HH:mm') : '—'),
      sorter: (a, b) => moment(a.sentAt).valueOf() - moment(b.sentAt).valueOf(),
    },
    {
      title: 'Message',
      dataIndex: 'messageText',
      key: 'messageText',
      ellipsis: {
        showTitle: false,
      },
      render: text => (
        <Tooltip placement='topLeft' title={text}>
          <MessageText>{text || '—'}</MessageText>
        </Tooltip>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'messageType',
      key: 'messageType',
      width: 120,
      render: messageType => (
        <Tag color={getSendTypeColor(messageType)}>
          {formatSendType(messageType)}
        </Tag>
      ),
    },
    {
      title: 'Recipients',
      key: 'recipients',
      width: 150,
      render: (_, record) => {
        const { successCount, failedCount } = getStatusCounts(record)
        if (failedCount > 0) {
          return (
            <RecipientsText>
              <span style={{ color: '#52c41a' }}>{successCount} sent</span>,{' '}
              <span style={{ color: '#ff4d4f' }}>{failedCount} failed</span>
            </RecipientsText>
          )
        }
        return (
          <RecipientsText style={{ color: '#52c41a' }}>
            {successCount} sent
          </RecipientsText>
        )
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          type='link'
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          aria-label={`View details for message sent ${moment(
            record.sentAt,
          ).format('DD MMM YYYY')}`}
        >
          View Details
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <TabWrapper>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </TabWrapper>
    )
  }

  if (messages.length === 0) {
    return (
      <TabWrapper>
        <EmptyStateCard>
          <EmptyStateIcon>
            <MessageOutlined />
          </EmptyStateIcon>
          <EmptyStateTitle>No messages have been sent yet</EmptyStateTitle>
          <EmptyStateText>
            Once you send broadcast messages to your customers, they&apos;ll
            appear here.
          </EmptyStateText>
        </EmptyStateCard>
      </TabWrapper>
    )
  }

  return (
    <TabWrapper>
      <InfoBanner>
        <InfoText>
          <strong>Note:</strong> This tab shows your broadcast messages only.
          One-to-one messages sent through the &quot;Message&quot; button
          aren&apos;t displayed here.
        </InfoText>
      </InfoBanner>
      <FiltersContainer>
        <FilterGroup>
          <Select
            value={dateFilter}
            onChange={setDateFilter}
            style={{ width: 200 }}
          >
            <Option value='all'>All Time</Option>
            <Option value='last30'>Last 30 Days</Option>
          </Select>
        </FilterGroup>
        <FilterGroup>
          <Input
            placeholder='Search by message text or venue name...'
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </FilterGroup>
      </FiltersContainer>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredMessages}
          rowKey='_id'
          pagination={
            filteredMessages.length > 50
              ? {
                  pageSize: 20,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} messages`,
                }
              : false
          }
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title='Message Details'
        visible={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false)
          setSelectedMessage(null)
        }}
        footer={[
          <Button key='close' onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        keyboard={true}
        maskClosable={true}
      >
        {selectedMessage && (
          <DetailsContent>
            <DetailsSection>
              <DetailsLabel>Sent At:</DetailsLabel>
              <DetailsValue>
                {moment(selectedMessage.sentAt).format('DD MMM YYYY, HH:mm')}
              </DetailsValue>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>Type:</DetailsLabel>
              <DetailsValue>
                <Tag color={getSendTypeColor(selectedMessage.messageType)}>
                  {formatSendType(selectedMessage.messageType)}
                </Tag>
              </DetailsValue>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>
                Recipients ({selectedMessage.venueIds?.length || 0}):
              </DetailsLabel>
              <RecipientsList>
                {selectedMessage.venueIds?.map((venue, index) => (
                  <RecipientItem key={venue?._id || index}>
                    <RecipientVenueName>
                      {venue?.title || `Venue ${index + 1}`}
                    </RecipientVenueName>
                  </RecipientItem>
                ))}
              </RecipientsList>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>Status:</DetailsLabel>
              <DetailsValue>
                {(() => {
                  const { successCount, failedCount } =
                    getStatusCounts(selectedMessage)
                  if (failedCount > 0) {
                    return (
                      <span>
                        <span style={{ color: '#52c41a' }}>
                          {successCount} sent
                        </span>
                        ,{' '}
                        <span style={{ color: '#ff4d4f' }}>
                          {failedCount} failed
                        </span>
                      </span>
                    )
                  }
                  return (
                    <span style={{ color: '#52c41a' }}>
                      {successCount} sent
                    </span>
                  )
                })()}
              </DetailsValue>
            </DetailsSection>

            <DetailsSection>
              <DetailsLabel>Message Text:</DetailsLabel>
              <MessageTextPreview>
                {selectedMessage.messageText}
              </MessageTextPreview>
            </DetailsSection>
          </DetailsContent>
        )}
      </Modal>
    </TabWrapper>
  )
}

const TabWrapper = styled.div`
  --sp-color-surface: ${supplierPortalTheme.colors.surface};
  --sp-color-text: ${supplierPortalTheme.colors.text};
  --sp-color-muted: ${supplierPortalTheme.colors.muted};
  --sp-color-border: ${supplierPortalTheme.colors.border};
  --sp-radius-card: ${supplierPortalTheme.radii.card};
  --sp-shadow-card: ${supplierPortalTheme.shadows.card};
  --sp-space-xs: ${supplierPortalTheme.spacing.xs};
  --sp-space-sm: ${supplierPortalTheme.spacing.sm};
  --sp-space-md: ${supplierPortalTheme.spacing.md};
  --sp-space-lg: ${supplierPortalTheme.spacing.lg};
  --sp-space-xl: ${supplierPortalTheme.spacing.xl};
  --sp-space-xxl: ${supplierPortalTheme.spacing.xxl};
  width: 100%;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-lg);
`

const Card = styled.div`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  padding: var(--sp-space-lg);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);

  .ant-table-thead > tr > th {
    background: #f7fafc;
    border-bottom: 1px solid var(--sp-color-border);
    font-weight: 600;
    color: var(--sp-color-text);
    padding: var(--sp-space-md);
  }

  .ant-table-tbody > tr {
    height: 48px;

    &:hover {
      background: #f7fafc;

      > td {
        background: #f7fafc !important;
      }
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--sp-color-border);
    padding: var(--sp-space-md);
  }
`

const InfoBanner = styled.div`
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: var(--sp-radius-control);
  padding: var(--sp-space-md);
`

const InfoText = styled.p`
  margin: 0;
  color: #0369a1;
  font-size: 14px;
  line-height: 1.5;
`

const FiltersContainer = styled.div`
  display: flex;
  gap: var(--sp-space-md);
  flex-wrap: wrap;
`

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
`

const MessageText = styled.span`
  max-width: 300px;
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const RecipientsText = styled.span`
  font-size: 14px;
`

const EmptyStateCard = styled.div`
  background: var(--sp-color-surface);
  border-radius: var(--sp-radius-card);
  padding: var(--sp-space-xxl);
  box-shadow: var(--sp-shadow-card);
  border: 1px solid var(--sp-color-border);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-space-sm);
  min-height: 320px;
  justify-content: center;
`

const EmptyStateIcon = styled.div`
  font-size: 48px;
  color: var(--sp-color-muted);
`

const EmptyStateTitle = styled.h3`
  margin: 0;
  color: var(--sp-color-text);
  font-size: 18px;
  font-weight: 600;
`

const EmptyStateText = styled.p`
  margin: 0;
  color: var(--sp-color-muted);
  font-size: 15px;
  line-height: 1.5;
`

const DetailsContent = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-md);
  padding: var(--sp-space-xs) 0;
`

const DetailsSection = styled.div`
  margin-bottom: var(--sp-space-md);

  &:last-child {
    margin-bottom: 0;
  }
`

const DetailsLabel = styled.div`
  font-weight: 600;
  color: var(--sp-color-text);
  margin-bottom: var(--sp-space-sm);
  font-size: 14px;
`

const DetailsValue = styled.div`
  color: var(--sp-color-text);
  font-size: 14px;
`

const RecipientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-space-sm);
  max-height: 200px;
  overflow-y: auto;
  padding: var(--sp-space-sm);
  background: #f8fafc;
  border-radius: 4px;
`

const RecipientItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const RecipientVenueName = styled.span`
  color: var(--sp-color-text);
  font-size: 14px;
`

const MessageTextPreview = styled.div`
  border: 1px solid var(--sp-color-border);
  border-radius: 4px;
  padding: var(--sp-space-md);
  background: #ffffff;
  max-height: 400px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.6;
`

export default SentMessagesTab
